"use client";

import React, { ReactNode, createContext, useContext, useCallback, useMemo } from "react";
import { Message } from "@/lib/core/crm/types";

// Simple state interface
export interface SimpleMessageState {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
}

// Simple context interface
export interface SimpleMessageContextType {
  state: SimpleMessageState;

  // Actions
  setMessages: (messages: Message[]) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  removeMessage: (messageId: string) => Message | null;
  restoreMessage: (message: Message) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Computed values
  unreadCount: number;
}

const SimpleMessageContext = createContext<SimpleMessageContextType | undefined>(undefined);

export function SimpleMessageProvider({ children }: { children: ReactNode }) {
  const [state, setState] = React.useState<SimpleMessageState>({
    messages: [],
    isLoading: false,
    error: null,
  });

  // Set messages
  const setMessages = useCallback((messages: Message[]) => {
    setState((prev) => ({ ...prev, messages }));
  }, []);

  // Update a specific message
  const updateMessage = useCallback((messageId: string, updates: Partial<Message>) => {
    setState((prev) => ({
      ...prev,
      messages: prev.messages.map((msg) => (msg.id === messageId ? { ...msg, ...updates } : msg)),
    }));
  }, []);

  // Remove a message (for optimistic updates)
  const removeMessage = useCallback((messageId: string) => {
    let removedMessage: Message | null = null;

    setState((prev) => {
      const messageToRemove = prev.messages.find((msg) => msg.id === messageId);
      if (messageToRemove) {
        removedMessage = messageToRemove;
        return {
          ...prev,
          messages: prev.messages.filter((msg) => msg.id !== messageId),
        };
      }
      return prev;
    });

    return removedMessage;
  }, []);

  // Restore a message (for rollback)
  const restoreMessage = useCallback((message: Message) => {
    setState((prev) => {
      const exists = prev.messages.find((msg) => msg.id === message.id);
      if (exists) return prev;

      return {
        ...prev,
        messages: [...prev.messages, message].sort(
          (a, b) => new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime()
        ),
      };
    });
  }, []);

  // Set loading state
  const setLoading = useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, isLoading: loading }));
  }, []);

  // Set error state
  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  // Compute unread count
  const unreadCount = useMemo(() => {
    return state.messages.filter((msg) => !msg.isRead).length;
  }, [state.messages]);

  // Memoize context value
  const contextValue = useMemo<SimpleMessageContextType>(
    () => ({
      state,
      setMessages,
      updateMessage,
      removeMessage,
      restoreMessage,
      setLoading,
      setError,
      unreadCount,
    }),
    [
      state,
      setMessages,
      updateMessage,
      removeMessage,
      restoreMessage,
      setLoading,
      setError,
      unreadCount,
    ]
  );

  return (
    <SimpleMessageContext.Provider value={contextValue}>{children}</SimpleMessageContext.Provider>
  );
}

// Custom hook to use the simple message context
export function useSimpleMessageContext(): SimpleMessageContextType {
  const context = useContext(SimpleMessageContext);
  if (context === undefined) {
    throw new Error("useSimpleMessageContext must be used within a SimpleMessageProvider");
  }
  return context;
}
